import ClassNames from "classnames";
import { FunctionComponent } from "react";
import { useAppSelector } from "../../store/hooks.js";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import placeholderStyles from "../../stylesheets/components/placeholder.module.css";
import styles from "../../stylesheets/components/webcam-module.module.css";
import {
  BaseModuleProps,
  ColorScheme,
  WebcamModuleSettings,
  WebcamSize,
} from "../../types/index.js";
import { css } from "../../utils/emotion.js";
import { getWebcamParentLayout } from "../../utils/utils.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";
import MultilineText from "../MultilineText.js";
import RichEditorWrapper from "../RichEditorWrapper.js";

// default "markAsUsed" does not work if there are multiple css modules imported
void placeholderStyles.Placeholder__Image;

interface Props extends BaseModuleProps<WebcamModuleSettings> {
  scheme: ColorScheme;
}

const WebcamModule: FunctionComponent<Props> = ({
  scheme,
  pageId,
  isFirstOnPage,
  isPreview,
  translatedModule,
  translatedModule: {
    id,
    settings: { url, textAlign, layoutVariant },
    translation: {
      languageId,
      settings: { title, subtitle, imageCaption },
    },
  },
  queries,
}) => {
  const parentLayout = useAppSelector((state) =>
    getWebcamParentLayout(state.modules, translatedModule),
  );

  const size: WebcamSize =
    parentLayout === "horizontal" || !queries["Query--medium"]
      ? "big"
      : translatedModule.settings.size;
  const subtitleIsSplit = queries["Query--medium"] && size === "small";

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={id}
      colors={{ background: scheme.main.background, color: scheme.main.text }}
      className={styles.WebcamModule}
    >
      <ModuleHeadings
        scheme={scheme}
        isFirstOnPage={isFirstOnPage}
        textAlign={textAlign}
        title={title}
        subtitle={subtitleIsSplit ? undefined : subtitle}
      />

      <div
        className={ClassNames(styles.WebcamModule__Inner, {
          [styles["WebcamModule__Inner--image-big"]]: size === "big",
          [styles["WebcamModule__Inner--image-small"]]: size === "small",
        })}
      >
        <figure
          className={ClassNames(styles.WebcamModule__Image, {
            [styles["WebcamModule__Image--image-last"]]:
              layoutVariant === "image-last",
          })}
        >
          {url ? (
            <img
              className={ClassNames("FullImage", styles.WebcamModule__Img)}
              loading="lazy"
              alt={imageCaption ?? ""}
              src={url}
              // It would be better to set width/height here, but the image
              // dimensions of the webcam image are unknown at this point.
            />
          ) : (
            <div className={placeholderStyles.Placeholder}>
              <img
                className={ClassNames(
                  "FullImage",
                  styles.WebcamModule__PlaceholderImg,
                )}
                loading="lazy"
                alt={imageCaption ?? ""}
                src="/image.svg"
                width="40"
                height="40"
              />
            </div>
          )}

          {imageCaption && (
            <figcaption
              className={ClassNames(
                styles.WebcamModule__ImageCaption,
                css({
                  backgroundColor: scheme.primary.background,
                  color: scheme.primary.text,
                }),
              )}
            >
              {imageCaption}
            </figcaption>
          )}
        </figure>

        <div className={styles.WebcamModule__Text}>
          {subtitle && subtitleIsSplit && (
            <div
              className={ClassNames(
                moduleBaseStyles.Module__Subtitle,
                styles.WebcamModule__Subtitle,
                `TextAlign--${textAlign.subtitle}`,
                css({ color: scheme.main.text }),
              )}
            >
              <MultilineText text={subtitle} />
            </div>
          )}

          <RichEditorWrapper
            module={translatedModule}
            pageId={pageId}
            languageId={languageId}
            isPreview={isPreview}
            scheme={scheme}
            className={`TextAlign--${textAlign.description}`}
          />
        </div>
      </div>
    </ModuleWithHeadings>
  );
};

export default WebcamModule;
